import {apiBindings} from "../../utils/constants";
import {useIntegrator} from "../../integrator.context";
import {RemoveFileArchiveCommand} from "../../commands/removeFileArchive.command";
import {ChangeConstantsCommand} from "../../commands/changeConstants.command";
import {computePixelMultiplier} from "../../utils/exportTransform";
import SoundAsset from "../../elements/soundAsset";
import IconButton from "../../elements/IconButton";
import {useApp} from "../../app.context";
import {downloadFile} from "../../utils/file.utils";

// Read a (cross-origin) image's natural dimensions; undefined if it can't load.
function loadDimensions(url: string): Promise<{w: number; h: number} | undefined> {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve({w: img.naturalWidth, h: img.naturalHeight});
        img.onerror = () => resolve(undefined);
        img.src = url;
    });
}

function ArchiveView() {
    const {state, executeCommand} = useIntegrator();
    const {confirmDelete} = useApp();

    function getFileType(fileName: string): string {
        return fileName.split('.')[1];
    }

    return (
        <div className={'layout vertical'} style={{gap: 10, flexGrow: 1}}>
            <div className={'layout horizontal gap'} style={{flexWrap: 'wrap', alignContent: 'flex-start', gap: 10, flexGrow: 1, overflow: 'auto', height: 0}}>
                {state.archive !== undefined && state.archive.map((file, index) => {
                    const url = `${apiBindings.images}${file.fileName}`;
                    const type = getFileType(file.fileName);
                    return (
                        <div key={index} style={{
                            position: 'relative',
                            width: 200, height: 200,
                            backgroundColor: 'var(--box-color)',
                            borderRadius: 'var(--border-radius)'
                        }}>
                            {type === 'png' && <div className={'layout vertical center'} style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%'
                            }}>
                                <img alt={file.fileName} style={{
                                    width: '150px'
                                }}
                                     src={url}
                                />
                                <p>{file.key}</p>
                            </div>}
                            <div style={{
                                position: 'absolute',
                                borderRadius: 'var(--border-radius)',
                                width: '100%',
                                height: '100%'
                            }}>
                                <div className={'layout horizontal gap'} style={{padding: 5}}>
                                    {type === 'wav' && <IconButton icon={'fa-solid fa-rotate-left'}
                                                title={'Restore — swap this back in as the live sound'}
                                                disabled={!(file.key && file.key !== 'none' && state.sounds?.[file.key] !== undefined)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (state.sounds?.[file.key] === undefined) return;
                                                    executeCommand(new ChangeConstantsCommand(data => {
                                                        const sound = data.sounds[file.key];
                                                        if (sound === undefined) return;
                                                        // Swap, as for images: the archived file becomes live, and the
                                                        // current one (if there is one) takes its place in the archive.
                                                        const displacedFileName = sound.fileName;
                                                        sound.fileName = file.fileName;
                                                        const idx = data.archive.findIndex(a => a.fileName === file.fileName);
                                                        if (idx >= 0) data.archive.splice(idx, 1);
                                                        if (displacedFileName) data.archive.push({fileName: displacedFileName, key: file.key});
                                                    }));
                                                }}/>}
                                    {type !== 'wav' && <IconButton icon={'fa-solid fa-rotate-left'}
                                                title={'Restore — swap this back in as the live image'}
                                                disabled={!(type === 'png' && file.key && file.key !== 'none' && state.images?.[file.key] !== undefined)}
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (state.images?.[file.key] === undefined) return;
                                                    const dims = await loadDimensions(url);
                                                    executeCommand(new ChangeConstantsCommand(data => {
                                                        const img = data.images[file.key];
                                                        if (img === undefined) return;
                                                        // Swap: archived file becomes live, current live file goes to archive.
                                                        const displacedFileName = img.fileName;
                                                        img.fileName = file.fileName;
                                                        if (dims) {
                                                            img.srcWidth = dims.w;
                                                            img.srcHeight = dims.h;
                                                            img.pixelMultiplier = computePixelMultiplier(dims.w, img.scale);
                                                        } else {
                                                            // Couldn't read dims — clear so export re-derives from the file.
                                                            img.srcWidth = undefined;
                                                            img.srcHeight = undefined;
                                                        }
                                                        const idx = data.archive.findIndex(a => a.fileName === file.fileName);
                                                        if (idx >= 0) data.archive.splice(idx, 1);
                                                        data.archive.push({fileName: displacedFileName, key: file.key});
                                                    }));
                                                }}/>}
                                    <IconButton icon={'fa-solid fa-trash'} onClick={async (e) => {
                                        e.stopPropagation();
                                        if (await confirmDelete(file.fileName, 'archive file'))
                                            executeCommand(new RemoveFileArchiveCommand(file.fileName));
                                    }}/>
                                    <IconButton icon={'fa-solid fa-download'} title={'Download this file'} onClick={(e) => {
                                        e.stopPropagation();
                                        // Named after its key, so a saved file says what it was.
                                        downloadFile(url, file.key && file.key !== 'none' ? `${file.key}_${file.fileName}` : file.fileName);
                                    }}/>
                                </div>
                            </div>
                            {type === 'wav' && <div className={'layout vertical center gap'} style={{
                                position: 'absolute',
                                margin: 50,
                            }}>
                                <SoundAsset id={file.fileName} size={100} upload={false}/>
                                <p>{file.key}</p>
                            </div>}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default ArchiveView;